/**
 * Agent definitions — the one shape every pluggable agent declares.
 *
 * Spec: docs/9-exploration/SPEC-custom-agent-template.md §2 (switch groups A–L).
 * Canon: doc 20 (Agent = Role + Goal + Permissions + Memory + Skill Stack +
 * Output Contract; six rules) and doc 22 (classification dimensions; voice,
 * never authority).
 *
 * A definition is the platform's half of an agent. The workspace's half lives
 * in `agent_settings` (enabled, model, prompt overlay, triggers, evidence
 * sources, delivery, limits, reference docs, behavior config, sponsor) and is
 * read through `src/lib/agentSettingsService.ts`. The definition states the
 * fixed parts and the defaults; the settings row holds what an administrator
 * changed.
 *
 * What a definition can never express: an authority verb. Outputs are
 * proposals, findings, reports, feedback, notifications and tasks. Accepting
 * any of them is a human act through an existing RPC or store path.
 */

import type { LucideIcon } from 'lucide-react';
import { Bot, CalendarClock, Compass as CompassIcon, Crosshair, GitMerge, KeyRound, Network, PenLine, Plane, RadioTower, Ruler, ShieldAlert, Swords } from 'lucide-react';
import type { AiCapabilityClass, AiEvidenceModel } from './aiCapabilityRegistry';
import type { RunScopeType } from '../agentRunService';
import {
  DEFAULT_VALUE_ESTIMATOR_CONFIG,
  ESTIMATE_VALUE_JOB,
  ESTIMATE_VALUE_SYSTEM_PROMPT,
  type ValueEstimatorBehaviorConfig,
} from '../../../supabase/functions/_shared/estimateValueCore.ts';
import { CURRENT_MODELS, DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';
import {
  CLASSIFY_JOB,
  CLASSIFY_SYSTEM_PROMPT,
  DEFAULT_CLASSIFY_CONFIG,
  type ClassifyBehaviorConfig,
} from '../../../supabase/functions/_shared/classifyCore.ts';
import {
  CLUSTER_JOB,
  CLUSTER_SYSTEM_PROMPT,
  DEFAULT_CLUSTER_CONFIG,
  type ClusterBehaviorConfig,
} from '../../../supabase/functions/_shared/clusterCore.ts';
import {
  DEFAULT_RELATIONSHIP_CONFIG,
  RELATIONSHIP_JOB,
  RELATIONSHIP_SYSTEM_PROMPT,
  type RelationshipBehaviorConfig,
} from '../../../supabase/functions/_shared/relationshipCore.ts';
import { DRAFT_ITEMS_JOB, DRAFT_ITEMS_SYSTEM_PROMPT } from '../../../supabase/functions/_shared/draftItemsCore.ts';
import { FINDINGS_JOB } from '../../../supabase/functions/_shared/findingsCore.ts';

// ── A. Identity ────────────────────────────────────────────────────────────────

export type AgentKind = 'built_in' | 'custom';
export type AgentStanding = 'worker' | 'participant';

/**
 * The built-in behaviors a custom agent can reuse (spec §2 J). A behavior is
 * the type-specific half of an agent: its job, what it may produce, its runner
 * and its own switches (agent_settings.behavior_config). Grows only when a
 * behavior has a runner on the template — never as an empty option.
 */
export type AgentBehaviorKind = 'estimate_value' | 'classify' | 'detect_cluster' | 'propose_relationship' | 'draft_items' | 'find_issues';

// ── C. What it reads ───────────────────────────────────────────────────────────

export type AgentPacket = 'item' | 'pool' | 'cycle' | 'session' | 'workspace';

/** The evidence sources the platform knows. Each carries its data-sent line. */
export type AgentEvidenceSourceKey =
  | 'workspace_record'
  | 'reference_docs'
  | 'connected_systems'
  | 'external_web';

export interface AgentEvidenceSource {
  key: AgentEvidenceSourceKey;
  label: string;
  /** What leaves the workspace when this source is on — the Trust Center line. */
  dataSent: string;
  /** Default for a fresh settings row; an administrator may change it. */
  defaultEnabled: boolean;
  /** When false the switch is shown but cannot be turned on yet. */
  available: boolean;
  unavailableReason?: string;
}

export type AgentMemory = 'turn' | 'run' | 'learned_rules';

// ── D. What it may produce ─────────────────────────────────────────────────────

export type AgentOutputKind =
  | 'proposal:value'
  | 'proposal:classification'
  | 'proposal:relationship'
  | 'proposal:cluster'
  | 'proposal:item'
  | 'proposal:text'
  | 'finding'
  | 'report'
  | 'inline_feedback'
  | 'notification'
  | 'task';

// ── E. When it runs ────────────────────────────────────────────────────────────

/** The platform's event catalog. A behavior declares the subset it supports. */
export type AgentEventKey =
  | 'item_created'
  | 'item_updated'
  | 'item_added_to_pool'
  | 'item_added_to_cycle'
  | 'readiness_failed'
  | 'connector_sync_completed'
  | 'session_scheduled'
  | 'priority_list_approved'
  | 'task_assigned';

/**
 * The platform's event catalog in words (spec §2 E). The platform owns it;
 * each behavior declares the subset it supports; an administrator turns each
 * supported event on or off per agent. Events are recorded in agent_events by
 * database triggers and watched by the ambient event_watch job.
 */
export const AGENT_EVENT_CATALOG: Record<AgentEventKey, { label: string; description: string }> = {
  item_created: { label: 'An item is created', description: 'Runs on each new item.' },
  item_updated: { label: 'An item\'s text changes', description: 'Runs when an item\'s title, description, category, altitude or owner changes.' },
  item_added_to_pool: { label: 'An item joins a pool', description: 'Runs on items added to any item pool.' },
  item_added_to_cycle: { label: 'An item joins a cycle', description: 'Runs on items added to a pool or track inside a cycle.' },
  readiness_failed: { label: 'An item fails readiness', description: 'Runs on items a readiness check held back.' },
  connector_sync_completed: { label: 'A connected tool finishes a sync', description: 'Runs over items changed since the last run when a connector finishes syncing.' },
  session_scheduled: { label: 'A session is scheduled', description: 'Runs on the items of a session when it is scheduled or rescheduled.' },
  priority_list_approved: { label: 'A priority list is approved', description: 'Runs on the items of an approved priority list.' },
  task_assigned: { label: 'A task is given to this agent', description: 'Runs on the task\'s items; the task completes when a person has reviewed what it produced.' },
};

export interface AgentTriggerDefaults {
  manualSingle: boolean;
  manualBulk: boolean;
  runNow: boolean;
  /** Events on by default — must be a subset of `supportedEvents`. */
  events: AgentEventKey[];
  /** Cadence in minutes (≥ 5), or null for no schedule. */
  scheduleMinutes: number | null;
  debounceSeconds: number;
}

// ── F. Where output goes ───────────────────────────────────────────────────────

export interface AgentDeliveryDefaults {
  /** Show its trigger and its outputs on the object (the item's Values section). */
  surfaceOnObject: boolean;
  /** Role slugs, plus 'object_people' (the items' owners) and 'admins'. */
  notifyRoles: string[];
  /** Proposals: the confidence (0–1) below which a proposal is not announced. */
  confidenceFloor?: number;
  /** Findings: the severity below which nothing is delivered. */
  severityFloor?: 'info' | 'low' | 'medium' | 'high' | 'critical';
  digest: 'per_run' | 'daily' | 'weekly';
  /** Deliveries from this agent per day, inside the workspace's own cap. */
  dailyCap: number;
  /** Ask a person when the agent cannot do its job (a clarify task on an abstain). */
  createTasks: boolean;
}

/** Who an agent can announce to, besides role holders. */
export const NOTIFY_AUDIENCES: { key: string; label: string }[] = [
  { key: 'object_people', label: 'The items\' owners' },
  { key: 'admins', label: 'Workspace administrators' },
];

// ── G. Review ──────────────────────────────────────────────────────────────────

export type AgentReviewVerb =
  | 'accept'
  | 'accept_with_change'
  | 'partial_accept'
  | 'reject'
  | 'dismiss'
  | 'reopen';

// ── K. Cost and limits ─────────────────────────────────────────────────────────

export interface AgentLimits {
  /** Key in ai_usage_counters (stored as 'agent:<key>'). Default: the agent type. */
  meterFeature: string;
  /** Model calls a month. */
  monthlyCap: number;
  perRunObjectCap: number;
  batchSize: number;
  timeoutMs: number;
  failurePosture: 'complete_with_errors' | 'fail_run';
}

/** The bound an administrator works inside on limits (§2 K: "per behavior"). */
export const LIMIT_BOUNDS = {
  monthlyCap: { min: 1, max: 100000 },
  perRunObjectCap: { min: 1, max: 1000 },
  batchSize: { min: 1, max: 25 },
  timeoutMs: { min: 5_000, max: 120_000 },
} as const;

// ── H. Model and prompt ────────────────────────────────────────────────────────

/** The models an administrator may pick (§2 H: "from the allowed list"): the current generation, one shared list. */
export const AGENT_MODELS: { id: string; name: string }[] = CURRENT_MODELS.map(({ id, name }) => ({ id, name }));

/** Output languages an agent can write prose in (the app's supported locales). */
export const AGENT_LANGUAGES: { id: string; name: string }[] = [
  { id: 'en', name: 'English' },
  { id: 'fr', name: 'French' },
];

// ── L. Lifecycle ───────────────────────────────────────────────────────────────

export interface AgentChange {
  version: number;
  date: string;
  change: string;
}

// ── The definition ─────────────────────────────────────────────────────────────

export interface AgentDefinition {
  // A. Identity
  agentType: string;
  /** Joins src/lib/ai/aiCapabilityRegistry.ts. */
  capabilityId: string;
  name: string;
  tagline: string;
  description: string;
  kind: AgentKind;
  /** The behavior this agent runs; a custom agent reuses one built-in behavior. */
  behaviorKind: AgentBehaviorKind;
  standing: AgentStanding;
  icon: LucideIcon;
  accent: { iconBg: string; iconText: string; chip: string };
  settingsHref: string;
  /** Its report page — every template agent has one (spec §5). */
  reportHref: string;

  // B. The job (canon doc 20)
  role: string;
  goal: string;
  outputContract: string;
  skillStack: string[];
  evidenceModel: AiEvidenceModel;
  capabilityClass: AiCapabilityClass;

  // C. What it reads
  packet: AgentPacket;
  evidenceSources: AgentEvidenceSource[];
  memory: AgentMemory;

  // D. What it may produce
  outputs: AgentOutputKind[];

  // E. When it runs
  supportedEvents: AgentEventKey[];
  triggers: AgentTriggerDefaults;
  scopeTypes: RunScopeType[];
  /** In words: what makes two runs the same fact, so the second is skipped. */
  idempotencyKey: string;

  // F. Where output goes
  delivery: AgentDeliveryDefaults;

  // G. Review and authority
  reviewRoles: string[];
  reviewVerbs: AgentReviewVerb[];
  bulkAcceptAllowed: boolean;

  // H. Model and prompt
  defaultModel: string;
  defaultSystemPrompt: string;
  /** The platform bound; an administrator may lower these, never raise them. */
  modelDefaults: { temperature: number; maxTokens: number };

  // I. Confidence and learning
  confidenceReporting: 'self_reported' | 'calibrated';
  learnedRules: boolean;

  // K. Cost and limits
  limits: AgentLimits;

  // L. Lifecycle
  definitionVersion: number;
  /** What changed in each version of the definition (platform-owned). */
  changeLog: AgentChange[];

  /** "How it works" bullets for the settings page. */
  howItWorks: string[];
}

// ── Completeness (canon doc 20 §6) ─────────────────────────────────────────────

/**
 * The six parts an agent must have before it ships, plus the switches the
 * template adds. Returns the names of whatever is missing; empty means complete.
 */
export function missingDefinitionParts(def: AgentDefinition): string[] {
  const missing: string[] = [];
  const text = (v: string | undefined) => typeof v === 'string' && v.trim().length > 0;
  if (!text(def.role)) missing.push('role');
  if (!text(def.goal)) missing.push('goal');
  if (!text(def.outputContract)) missing.push('output contract');
  if (!def.skillStack?.length) missing.push('skill stack');
  if (!def.memory) missing.push('memory scope');
  if (!def.outputs?.length) missing.push('outputs (permission boundary)');
  if (!def.evidenceSources?.length) missing.push('evidence sources');
  if (!def.reviewRoles?.length) missing.push('review roles');
  if (!def.limits?.meterFeature) missing.push('meter feature');
  if (!(def.limits?.monthlyCap > 0)) missing.push('monthly cap');
  if (!def.modelDefaults) missing.push('model bound');
  const latest = def.changeLog?.[def.changeLog.length - 1];
  if (!latest || latest.version !== def.definitionVersion) missing.push('change log entry for the current version');
  const unsupported = def.triggers?.events?.filter((e) => !def.supportedEvents.includes(e)) ?? [];
  if (unsupported.length) missing.push(`default events outside the supported set: ${unsupported.join(', ')}`);
  return missing;
}

/**
 * The runner refuses any write the definition does not declare (spec §2 D).
 * Call it before every write an agent makes.
 */
export class AgentOutputRefused extends Error {}
export function assertAgentMayProduce(def: Pick<AgentDefinition, 'name' | 'outputs'>, kind: AgentOutputKind): void {
  if (!def.outputs.includes(kind)) {
    throw new AgentOutputRefused(`${def.name} does not produce ${kind}; nothing was written.`);
  }
}

/**
 * What a template agent's settings row holds the first time it is saved: the
 * definition's defaults for every switch group, so what the page shows is
 * what the database enforces (reviewers, delivery, limits) from the start.
 */
export function templateSettingsDefaults(def: AgentDefinition): {
  behaviorKind: AgentBehaviorKind;
  reviewRoles: string[];
  delivery: Record<string, unknown>;
  limits: Record<string, unknown>;
  triggers: Record<string, unknown>;
  outputs: string[];
} {
  return {
    behaviorKind: def.behaviorKind,
    reviewRoles: [...def.reviewRoles],
    delivery: { ...def.delivery },
    limits: { ...def.limits, meterFeature: def.kind === 'custom' ? def.agentType : def.limits.meterFeature },
    triggers: { ...def.triggers, taskAssigned: def.supportedEvents.includes('task_assigned') },
    outputs: [...def.outputs],
  };
}

// ── Value Estimator (PRD-value-suggestion-agent.md) ────────────────────────────

// The behavior's own switches and its contract are the shared core (one copy
// with the server runtime): supabase/functions/_shared/estimateValueCore.ts.
export { DEFAULT_VALUE_ESTIMATOR_CONFIG, type ValueEstimatorBehaviorConfig };
export const VALUE_ESTIMATOR_SYSTEM_PROMPT = ESTIMATE_VALUE_SYSTEM_PROMPT;

export const VALUE_ESTIMATOR_AGENT_TYPE = 'value_estimator';

export const VALUE_ESTIMATOR: AgentDefinition = {
  agentType: VALUE_ESTIMATOR_AGENT_TYPE,
  capabilityId: 'value_estimator',
  name: 'Value Estimator',
  tagline: 'Proposes a value for an item, with the evidence it came from',
  description:
    'Proposes an attribute value (effort first) for an item from the item text, this workspace\'s ' +
    'accepted values on similar items, and any reference documents an administrator adds. Each ' +
    'proposal names its evidence and states a confidence. When the item is too thin to estimate, ' +
    'it says so and names what is missing. A person accepts, changes, or rejects every proposal; ' +
    'what they decide teaches the next one.',
  kind: 'built_in',
  behaviorKind: 'estimate_value',
  standing: 'worker',
  icon: Ruler,
  accent: { iconBg: 'bg-teal-100', iconText: 'text-teal-700', chip: 'bg-teal-50 text-teal-700 border-teal-200' },
  settingsHref: '/prioritization/settings/value-estimator-agent',
  reportHref: '/prioritization/agents/value_estimator',

  role: ESTIMATE_VALUE_JOB.role,
  goal: ESTIMATE_VALUE_JOB.goal,
  outputContract: ESTIMATE_VALUE_JOB.outputContract,
  skillStack: [...ESTIMATE_VALUE_JOB.skillStack],
  evidenceModel: 'llm_primary',
  capabilityClass: 'shaping',

  packet: 'item',
  evidenceSources: [
    {
      key: 'workspace_record',
      label: 'This workspace\'s accepted values',
      dataSent: 'Titles, descriptions, category and altitude of similar items, with their accepted values for the attribute.',
      defaultEnabled: true,
      available: true,
    },
    {
      key: 'reference_docs',
      label: 'Reference documents',
      dataSent: 'The sizing guide and examples an administrator uploads on this page. Each document is capped at 4,000 characters and cleaned of invisible characters before it is sent.',
      defaultEnabled: true,
      available: true,
    },
    {
      key: 'connected_systems',
      label: 'Connected history (Jira)',
      dataSent: 'Keys, summaries, story points, estimates, time spent, dates and status of the item\'s linked Jira issues and of issues in the linked project whose text matches the item\'s title. Read through the integrations engine; nothing is written to Jira.',
      defaultEnabled: false,
      available: true,
    },
  ],
  memory: 'run',

  // A value proposal; a clarify task for the item's owner when it cannot
  // estimate (when the administrator turns that on); the record-actual ask;
  // and the announcement of what it proposed.
  outputs: ['proposal:value', 'task', 'notification'],

  supportedEvents: ['item_created', 'item_updated', 'item_added_to_pool', 'item_added_to_cycle', 'connector_sync_completed', 'task_assigned'],
  triggers: {
    manualSingle: true,
    manualBulk: true,
    runNow: true,
    events: [],
    scheduleMinutes: null,
    debounceSeconds: 120,
  },
  scopeTypes: ['all_empty', 'since_last_run', 'date_range', 'pool', 'cycle', 'track', 'collection'],
  idempotencyKey: 'item + attribute + scale while an agent proposal is still open or a value is accepted',

  delivery: {
    surfaceOnObject: true,
    notifyRoles: ['object_people'],
    confidenceFloor: 0.5,
    digest: 'per_run',
    dailyCap: 20,
    createTasks: false,
  },

  reviewRoles: ['item-manager'],
  reviewVerbs: ['accept', 'accept_with_change', 'reject'],
  bulkAcceptAllowed: true,

  defaultModel: DEFAULT_MODEL,
  defaultSystemPrompt: VALUE_ESTIMATOR_SYSTEM_PROMPT,
  modelDefaults: { ...ESTIMATE_VALUE_JOB.modelDefaults },

  confidenceReporting: 'self_reported',
  learnedRules: true,

  limits: {
    meterFeature: 'value_estimator',
    monthlyCap: 1000,
    perRunObjectCap: 200,
    batchSize: 1,
    timeoutMs: 45_000,
    failurePosture: 'complete_with_errors',
  },

  definitionVersion: 2,
  changeLog: [
    { version: 1, date: '2026-10-13', change: 'First agent on the template: effort proposals from precedent, documents and connected history, with calibration and learned rules.' },
    { version: 2, date: '2026-10-15', change: 'The workspace prompt is an overlay on the built-in contract, not a replacement; model parameters, reviewers, delivery, limits and server-side triggers are switches.' },
  ],

  howItWorks: [
    'Before any model call, a deterministic clarity check reads the title and description. A thin item gets an abstain that names what is missing, not a guess.',
    'Evidence is gathered in trust order: accepted values on similar items in this workspace, then reference documents, then the model alone — and a model-only proposal is marked "no precedent found".',
    'The proposal lands in the item\'s Values section like any other proposal: accept, change, or reject. Nothing is accepted without a person\'s name on it: a standing rule on a value accepts in the name of the administrator who set it.',
    'Every proposal writes a decision record with its evidence and the prompt version, so "why did you say 5?" is answered from the record, never by asking the model again.',
    'What people accept, change and reject is stamped on that record and becomes precedent for the next similar item.',
  ],
};

// ── Air Traffic Controller (classify) ─────────────────────────────────────────

// The behavior's switches, prompt and contract are the shared core (one copy
// with the server runtime): supabase/functions/_shared/classifyCore.ts.
export { DEFAULT_CLASSIFY_CONFIG, type ClassifyBehaviorConfig };

export const AIR_TRAFFIC_CONTROLLER_AGENT_TYPE = 'atc';

export const AIR_TRAFFIC_CONTROLLER: AgentDefinition = {
  agentType: AIR_TRAFFIC_CONTROLLER_AGENT_TYPE,
  capabilityId: 'atc',
  name: 'Air Traffic Controller',
  tagline: 'Proposes a category and altitude for each item',
  description:
    'Reads an item\'s title and description and proposes its category (problem, opportunity, risk, ' +
    'solution, commitment or investment), the altitude it sits at (1–5, from three scoping questions) ' +
    'and how the work would be carried out by people and AI. A person accepts, accepts part, or ' +
    'rejects every proposal.',
  kind: 'built_in',
  behaviorKind: 'classify',
  standing: 'worker',
  icon: Plane,
  accent: { iconBg: 'bg-[var(--slate-dim)]', iconText: 'text-[var(--text-sec)]', chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--rule)]' },
  settingsHref: '/prioritization/settings/atc-agent',
  reportHref: '/prioritization/agents/atc',

  role: CLASSIFY_JOB.role,
  goal: CLASSIFY_JOB.goal,
  outputContract: CLASSIFY_JOB.outputContract,
  skillStack: [...CLASSIFY_JOB.skillStack],
  evidenceModel: 'llm_primary',
  capabilityClass: 'shaping',

  packet: 'item',
  evidenceSources: [
    {
      key: 'workspace_record',
      label: 'The item itself',
      dataSent: 'The item\'s title and description, with the category definitions and altitude questions.',
      defaultEnabled: true,
      available: true,
    },
    {
      key: 'reference_docs',
      label: 'Reference documents',
      dataSent: 'The category examples and altitude guidance an administrator adds on this page. Each document is capped at 4,000 characters and cleaned of invisible characters before it is sent.',
      defaultEnabled: true,
      available: true,
    },
  ],
  memory: 'turn',

  // A classification proposal, and the announcement of what it proposed.
  outputs: ['proposal:classification', 'notification'],

  supportedEvents: ['item_created', 'item_updated', 'item_added_to_pool', 'connector_sync_completed', 'task_assigned'],
  // Runs by itself on each new item from the moment it is switched on (roadmap N-20);
  // every proposal still waits for a person.
  triggers: {
    manualSingle: true,
    manualBulk: true,
    runNow: true,
    events: ['item_created'],
    scheduleMinutes: null,
    debounceSeconds: 0,
  },
  scopeTypes: ['all_empty', 'since_last_run', 'date_range', 'pool', 'cycle', 'track', 'collection'],
  idempotencyKey: 'item while a classification proposal is open, or while it is classified (unless the agent is set to re-propose)',

  delivery: {
    surfaceOnObject: true,
    notifyRoles: [],
    digest: 'per_run',
    dailyCap: 20,
    createTasks: false,
  },

  reviewRoles: ['item-manager'],
  reviewVerbs: ['accept', 'partial_accept', 'reject'],
  bulkAcceptAllowed: false,

  defaultModel: DEFAULT_MODEL,
  defaultSystemPrompt: CLASSIFY_SYSTEM_PROMPT,
  modelDefaults: { ...CLASSIFY_JOB.modelDefaults },

  confidenceReporting: 'self_reported',
  learnedRules: false,

  limits: {
    meterFeature: 'atc',
    monthlyCap: 1000,
    perRunObjectCap: 200,
    batchSize: 1,
    timeoutMs: 45_000,
    failurePosture: 'complete_with_errors',
  },

  definitionVersion: 3,
  changeLog: [
    { version: 1, date: '2026-04-01', change: 'Hand-built agent: category, altitude and execution profile proposals, with its own settings table.' },
    { version: 2, date: '2026-10-15', change: 'Moved onto the agent template: common switches, server-side events, reviewers enforced in the database, decision records, and the workspace prompt as an overlay rather than a replacement.' },
    { version: 3, date: '2026-10-28', change: 'Runs by itself: "An item is created" starts on, so each new item gets a proposal that waits for a person.' },
  ],

  howItWorks: [
    'It reads the item\'s title and description, plus any reference documents an administrator adds, and answers the three altitude questions for the category it chooses.',
    'The altitude is the average of the three answers — reversibility, how many groups must align, and how far the work reaches — so a person can see exactly where it came from.',
    'The proposal shows on the item: accept it, accept only the category or only the altitude, or reject it. Nothing is ever applied without a person.',
    'Every proposal writes a decision record with the model and prompt version, and what the reviewer decided is stamped on that record.',
    'Once it is on, it proposes for each new item by itself, on the server, as the administrator who switched it on. Turn off "An item is created" to stop that.',
  ],
};

// ── Twins (detect_cluster) ───────────────────────────────────────────────────

// The behavior's switches, prompt and contract are the shared core (one copy
// with the server runtime): supabase/functions/_shared/clusterCore.ts.
export { DEFAULT_CLUSTER_CONFIG, type ClusterBehaviorConfig };

export const TWINS_AGENT_TYPE = 'twins';

export const TWINS: AgentDefinition = {
  agentType: TWINS_AGENT_TYPE,
  capabilityId: 'twins',
  name: 'Twins',
  tagline: 'Finds duplicate and variant items',
  description:
    'Reads items in batches and proposes groups that say the same thing in different words ' +
    '(duplicates) or are close variations on one theme (variants). A person accepts a group — the ' +
    'items become an unresolved cluster to resolve — or dismisses it. Nothing is merged automatically.',
  kind: 'built_in',
  behaviorKind: 'detect_cluster',
  standing: 'worker',
  icon: GitMerge,
  accent: { iconBg: 'bg-[var(--mustard-dim)]', iconText: 'text-[var(--mustard)]', chip: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]' },
  settingsHref: '/prioritization/settings/twins-agent',
  reportHref: '/prioritization/agents/twins',

  role: CLUSTER_JOB.role,
  goal: CLUSTER_JOB.goal,
  outputContract: CLUSTER_JOB.outputContract,
  skillStack: [...CLUSTER_JOB.skillStack],
  evidenceModel: 'llm_primary',
  capabilityClass: 'shaping',

  packet: 'pool',
  evidenceSources: [
    {
      key: 'workspace_record',
      label: 'The items it compares',
      dataSent: 'Titles and descriptions of the items in each batch (up to 40 at a time).',
      defaultEnabled: true,
      available: true,
    },
    {
      key: 'reference_docs',
      label: 'Reference documents',
      dataSent: 'Anything an administrator adds on this page about what counts as the same work here. Each document is capped at 4,000 characters and cleaned of invisible characters before it is sent.',
      defaultEnabled: true,
      available: true,
    },
  ],
  memory: 'run',

  // A cluster proposal, and the announcement of what it found.
  outputs: ['proposal:cluster', 'notification'],

  supportedEvents: ['item_created', 'item_updated', 'item_added_to_pool', 'connector_sync_completed', 'task_assigned'],
  triggers: {
    manualSingle: false,
    manualBulk: true,
    runNow: true,
    events: [],
    scheduleMinutes: null,
    debounceSeconds: 120,
  },
  scopeTypes: ['all_empty', 'since_last_run', 'date_range', 'pool', 'cycle', 'track', 'collection'],
  idempotencyKey: 'the same set of items while a proposal for them is open',

  delivery: {
    surfaceOnObject: true,
    notifyRoles: ['object_people'],
    digest: 'per_run',
    dailyCap: 20,
    createTasks: false,
  },

  reviewRoles: ['item-manager'],
  reviewVerbs: ['accept', 'dismiss', 'reopen'],
  bulkAcceptAllowed: false,

  defaultModel: DEFAULT_MODEL,
  defaultSystemPrompt: CLUSTER_SYSTEM_PROMPT,
  modelDefaults: { ...CLUSTER_JOB.modelDefaults },

  confidenceReporting: 'self_reported',
  learnedRules: false,

  limits: {
    meterFeature: 'twins',
    monthlyCap: 500,
    perRunObjectCap: 400,
    batchSize: 1,
    timeoutMs: 60_000,
    failurePosture: 'complete_with_errors',
  },

  definitionVersion: 2,
  changeLog: [
    { version: 1, date: '2026-04-01', change: 'Hand-built agent: duplicate and variant clusters in batches of 40, with its own settings table and report page.' },
    { version: 2, date: '2026-10-15', change: 'Moved onto the agent template: common switches, server-side events that check new items against the rest, one write path for proposals, reviewers enforced in the database, decision records, and the workspace prompt as an overlay.' },
  ],

  howItWorks: [
    'It reads items 40 at a time and returns only groups of two or more that say the same thing (duplicates) or are close variations on one theme (variants), each with a one- or two-sentence reason.',
    'Run over a scope, it compares the items in that scope. On an event, it checks the new items against the rest of the workspace — the most similar first — so a new item and its likely twin are read together.',
    'A person accepts a group — the items become an unresolved cluster to resolve as duplicate, variant or reframe — or dismisses it. Nothing is merged automatically.',
    'The same set of items is never proposed twice while a proposal for it is open. Every proposal writes a decision record; what the reviewer decided is stamped on it.',
  ],
};

// ── Nexus (propose_relationship) ─────────────────────────────────────────────

// The behavior's switches, prompt and contract are the shared core (one copy
// with the server runtime): supabase/functions/_shared/relationshipCore.ts.
export { DEFAULT_RELATIONSHIP_CONFIG, type RelationshipBehaviorConfig };

export const NEXUS_AGENT_TYPE = 'nexus';

export const NEXUS: AgentDefinition = {
  agentType: NEXUS_AGENT_TYPE,
  capabilityId: 'nexus_batch',
  name: 'Nexus',
  tagline: 'Finds how items relate to each other',
  description:
    'Reads items in batches and proposes structural relationships between pairs — what depends on ' +
    'what, what is part of what, which items must move together — each with a direction and a reason. ' +
    'A person accepts a proposal, which creates the relationship, or dismisses it. On an item it also ' +
    'answers "how does this relate?" in plain words.',
  kind: 'built_in',
  behaviorKind: 'propose_relationship',
  standing: 'worker',
  icon: Network,
  accent: { iconBg: 'bg-[var(--slate-dim)]', iconText: 'text-[var(--text-sec)]', chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--rule)]' },
  settingsHref: '/prioritization/settings/nexus-agent',
  reportHref: '/prioritization/agents/nexus',

  role: RELATIONSHIP_JOB.role,
  goal: RELATIONSHIP_JOB.goal,
  outputContract: RELATIONSHIP_JOB.outputContract,
  skillStack: [...RELATIONSHIP_JOB.skillStack],
  evidenceModel: 'llm_primary',
  capabilityClass: 'shaping',

  packet: 'pool',
  evidenceSources: [
    {
      key: 'workspace_record',
      label: 'The items it compares',
      dataSent: 'Titles, descriptions and categories of the items in each batch (up to 25 at a time).',
      defaultEnabled: true,
      available: true,
    },
    {
      key: 'reference_docs',
      label: 'Reference documents',
      dataSent: 'Anything an administrator adds on this page about how work fits together here. Each document is capped at 4,000 characters and cleaned of invisible characters before it is sent.',
      defaultEnabled: true,
      available: true,
    },
  ],
  memory: 'run',

  // A relationship proposal, and the announcement of what it found.
  outputs: ['proposal:relationship', 'notification'],

  supportedEvents: ['item_created', 'item_updated', 'item_added_to_pool', 'item_added_to_cycle', 'connector_sync_completed', 'task_assigned'],
  // Runs by itself on each new item from the moment it is switched on (roadmap N-20);
  // every proposal still waits for a person.
  triggers: {
    manualSingle: true,
    manualBulk: true,
    runNow: true,
    events: ['item_created'],
    scheduleMinutes: null,
    debounceSeconds: 120,
  },
  scopeTypes: ['all_empty', 'since_last_run', 'date_range', 'pool', 'cycle', 'track', 'collection'],
  idempotencyKey: 'the same pair of items and relationship type, either way round, while a proposal for it is open',

  delivery: {
    surfaceOnObject: true,
    notifyRoles: ['object_people'],
    confidenceFloor: 0,
    digest: 'per_run',
    dailyCap: 20,
    createTasks: false,
  },

  reviewRoles: ['relationship-curator'],
  reviewVerbs: ['accept', 'dismiss', 'reopen'],
  bulkAcceptAllowed: false,

  defaultModel: DEFAULT_MODEL,
  defaultSystemPrompt: RELATIONSHIP_SYSTEM_PROMPT,
  modelDefaults: { ...RELATIONSHIP_JOB.modelDefaults },

  confidenceReporting: 'self_reported',
  learnedRules: false,

  limits: {
    meterFeature: 'nexus',
    monthlyCap: 500,
    perRunObjectCap: 300,
    batchSize: 1,
    timeoutMs: 60_000,
    failurePosture: 'complete_with_errors',
  },

  definitionVersion: 4,
  changeLog: [
    { version: 1, date: '2026-04-01', change: 'Hand-built agent: a relationship advisor on the item and a batch scan in groups of 25, with its own settings table and report page.' },
    { version: 2, date: '2026-10-15', change: 'Moved onto the agent template: common switches, server-side events that check new items against the rest, one write path for every relationship proposal, reviewers enforced in the database, decision records, and the workspace prompt as an overlay on both the scan and the advisor.' },
    { version: 3, date: '2026-10-16', change: 'Accepting a proposal creates the relationship (or a package of the two items) in the same step, as Twins\' accept creates its cluster.' },
    { version: 4, date: '2026-10-28', change: 'Runs by itself: "An item is created" starts on, so each new item is checked against the rest and every proposal waits for a person.' },
  ],

  howItWorks: [
    'It reads items 25 at a time and proposes only relationships it has evidence for: dependency, prerequisite, aggregation, breakdown, elaboration, package or related — each with a direction and a one- or two-sentence reason.',
    'It never proposes duplicates or variants; those are Twins\' job.',
    'Run over a scope, it compares the items in that scope. On an event, it checks the new items against the rest of the workspace — the most similar first.',
    'Once it is on, it checks each new item by itself, on the server, as the administrator who switched it on. Turn off "An item is created" to stop that.',
    'A person accepts or dismisses each proposal; accepting creates the relationship (or a package of the two items). The same pair and type is never proposed twice while a proposal for it is open.',
    'On an item, "Find how it relates to other items" asks it in plain words; that answer writes nothing.',
  ],
};

// ── Scribe (draft_items) ─────────────────────────────────────────────────────

export const SCRIBE_AGENT_TYPE = 'scribe';

export const SCRIBE: AgentDefinition = {
  agentType: SCRIBE_AGENT_TYPE,
  capabilityId: 'scribe',
  name: 'Scribe',
  tagline: 'Turns what you type or say into draft items',
  description:
    'On the Scribe page, a person types or dictates free-form text — a meeting note, a voice memo — and ' +
    'Scribe drafts one item per piece of work in it, with a category, an altitude and a reason. The ' +
    'person keeps, edits or skips each draft; nothing is created until they do.',
  kind: 'built_in',
  behaviorKind: 'draft_items',
  standing: 'worker',
  icon: PenLine,
  accent: { iconBg: 'bg-[var(--slate-dim)]', iconText: 'text-[var(--text-sec)]', chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--rule)]' },
  settingsHref: '/prioritization/settings/scribe-agent',
  reportHref: '/prioritization/agents/scribe',

  role: DRAFT_ITEMS_JOB.role,
  goal: DRAFT_ITEMS_JOB.goal,
  outputContract: DRAFT_ITEMS_JOB.outputContract,
  skillStack: [...DRAFT_ITEMS_JOB.skillStack],
  evidenceModel: 'llm_primary',
  capabilityClass: 'shaping',

  packet: 'item',
  evidenceSources: [
    {
      key: 'workspace_record',
      label: 'The text the person gives it',
      dataSent: 'Only the text typed or dictated on the Scribe page.',
      defaultEnabled: true,
      available: true,
    },
    {
      key: 'reference_docs',
      label: 'Reference documents',
      dataSent: 'Anything an administrator adds on this page about how this workspace names and sizes work. Each document is capped at 4,000 characters and cleaned of invisible characters before it is sent.',
      defaultEnabled: true,
      available: true,
    },
  ],
  memory: 'turn',

  // Draft items the person keeps, edits or skips in the session. They are not
  // stored as proposals: the person creates the items they keep.
  outputs: ['proposal:item'],

  supportedEvents: [],
  triggers: {
    manualSingle: true,
    manualBulk: false,
    runNow: false,
    events: [],
    scheduleMinutes: null,
    debounceSeconds: 0,
  },
  scopeTypes: [],
  idempotencyKey: 'none — each use drafts from the text given; nothing is stored until a person creates it',

  delivery: {
    surfaceOnObject: false,
    notifyRoles: [],
    digest: 'per_run',
    dailyCap: 0,
    createTasks: false,
  },

  // The person who used it decides; creating an item needs their own right to create items.
  reviewRoles: ['idea-submitter'],
  reviewVerbs: ['accept', 'accept_with_change', 'dismiss'],
  bulkAcceptAllowed: false,

  defaultModel: DEFAULT_MODEL,
  defaultSystemPrompt: DRAFT_ITEMS_SYSTEM_PROMPT,
  modelDefaults: { ...DRAFT_ITEMS_JOB.modelDefaults },

  confidenceReporting: 'self_reported',
  learnedRules: false,

  limits: {
    meterFeature: 'scribe',
    monthlyCap: 2000,
    perRunObjectCap: 50,
    batchSize: 1,
    timeoutMs: 45_000,
    failurePosture: 'complete_with_errors',
  },

  definitionVersion: 2,
  changeLog: [
    { version: 1, date: '2026-04-01', change: 'Hand-built agent: free-form text to draft items, with its own settings table.' },
    { version: 2, date: '2026-10-16', change: 'Moved onto the agent template: common settings page, model parameters, reference documents, monthly cap, and the workspace prompt as an overlay rather than a replacement.' },
  ],

  howItWorks: [
    'On the Scribe page, type or dictate anything that mentions work — a meeting note, a list, a voice memo.',
    'Scribe drafts one item for each distinct piece of work in the text, with a category, an altitude from the three scoping questions, a confidence and a reason. It never adds items the text does not mention.',
    'Keep, edit or skip each draft, then create the ones you kept. Nothing is created until you do, and each item is created as yours.',
  ],
};
// ── The intelligence agents (find_issues) ────────────────────────────────────

/**
 * The parts every finding agent shares: detection is exact maths over the
 * workspace record (the shared cores); a model only words a finding already
 * computed. They scan the whole workspace, so an event or a schedule says when,
 * not which items. A finding is advice; accepting it changes nothing by itself.
 */
function findingAgent(p: {
  agentType: string;
  name: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  accent: { iconBg: string; iconText: string; chip: string };
  settingsHref: string;
  role: string;
  goal: string;
  defaultSystemPrompt: string;
  howItWorks: string[];
  /** What differs for a finder that is not exact-maths-plus-narration (Beacon reads the news; Review Due uses no model). */
  overrides?: Partial<AgentDefinition>;
}): AgentDefinition {
  return {
    agentType: p.agentType,
    capabilityId: p.agentType,
    name: p.name,
    tagline: p.tagline,
    description: p.description,
    kind: 'built_in',
    behaviorKind: 'find_issues',
    standing: 'worker',
    icon: p.icon,
    accent: p.accent,
    settingsHref: p.settingsHref,
    reportHref: agentReportHref(p.agentType),
    role: p.role,
    goal: p.goal,
    outputContract: FINDINGS_JOB.outputContract,
    skillStack: [...FINDINGS_JOB.skillStack],
    evidenceModel: 'deterministic_narration',
    capabilityClass: 'governance_drift',
    packet: 'workspace',
    evidenceSources: [
      {
        key: 'workspace_record',
        label: 'The workspace record it checks',
        dataSent: 'Nothing leaves the workspace for detection. To word a finding, its computed evidence (numbers and item names) is sent to the model.',
        defaultEnabled: true,
        available: true,
      },
    ],
    memory: 'run',
    outputs: ['finding', 'notification'],
    supportedEvents: ['item_updated', 'item_added_to_cycle', 'priority_list_approved', 'connector_sync_completed'],
    triggers: { manualSingle: false, manualBulk: true, runNow: true, events: [], scheduleMinutes: 1440, debounceSeconds: 120 },
    scopeTypes: ['all_empty'],
    idempotencyKey: 'each run replaces the agent\'s open findings with what it finds now; reviewed findings are kept',
    delivery: { surfaceOnObject: true, notifyRoles: ['admins'], severityFloor: 'high', digest: 'per_run', dailyCap: 20, createTasks: false },
    reviewRoles: ['strategist', 'decision-authority'],
    reviewVerbs: ['accept', 'dismiss', 'reopen'],
    bulkAcceptAllowed: false,
    defaultModel: DEFAULT_MODEL,
    defaultSystemPrompt: p.defaultSystemPrompt,
    modelDefaults: { ...FINDINGS_JOB.modelDefaults },
    confidenceReporting: 'self_reported',
    learnedRules: false,
    limits: { meterFeature: p.agentType, monthlyCap: 500, perRunObjectCap: 200, batchSize: 1, timeoutMs: 60_000, failurePosture: 'complete_with_errors' },
    definitionVersion: 2,
    changeLog: [
      { version: 1, date: '2026-07-10', change: 'Intelligence agent: exact detection with optional narration, run nightly by the workspace\'s intelligence scan.' },
      { version: 2, date: '2026-10-16', change: 'Moved onto the agent template: its own schedule, events and Run now; reviewers enforced in the database; a decision record per finding; a monthly cap on narration; the workspace prompt as an overlay.' },
    ],
    howItWorks: p.howItWorks,
    ...p.overrides,
  };
}

export const CASSANDRA_AGENT: AgentDefinition = findingAgent({
  agentType: 'cassandra',
  name: 'Fracture',
  tagline: 'Decision red-team — exposes fragile rankings before you publish',
  description:
    'Stress-tests a ranking that looks settled. Fracture reads the raw per-criterion, per-participant scores a session already produced and computes — exactly — where the order is fragile: knife-edge pairs a small weight shift would flip, top priorities propped up by a single criterion, and broadly-liked items hiding a minority that scored them near the floor. Each finding is reviewed by a human before it changes anything.',
  icon: Swords,
  accent: { iconBg: 'bg-rose-100', iconText: 'text-rose-600', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  settingsHref: '/prioritization/settings/fracture-agent',
  role: "Stress-test a ranking that looks settled and show where the order is fragile.",
  goal: "Stop a fragile ranking from being published as if it were firm.",
  defaultSystemPrompt:
    'You are Fracture, a decision red-team analyst. You explain pre-computed fragility evidence to executives in plain, specific language. Never invent numbers; lead with the surprising fact.',
  howItWorks: [
    'Detection is exact maths over group_scores and criteria weights — the finding never depends on a model being available.',
    'Knife-edge: two adjacent priorities within a razor-thin margin that a small weight shift would reverse.',
    'Single-criterion: a top priority whose rank collapses when its dominant criterion is removed.',
    'Hidden dissent: a broadly-liked item that a minority of participants scored near the floor — a late veto waiting to happen.',
  ],
});

export const COMPASS_AGENT: AgentDefinition = findingAgent({
  agentType: 'compass',
  name: 'Compass',
  tagline: 'Strategy alignment — catches where priorities drift from stated strategy',
  description:
    'Holds the priority list up against what leadership said the strategy is. Compass reads your strategic objectives and their links to priorities, then surfaces the gaps: critical objectives with nothing driving them, objectives nominally covered but owned by no one, active items serving no objective at all, and effort concentrated on a low-priority goal while a critical one starves.',
  icon: CompassIcon,
  accent: { iconBg: 'bg-sky-100', iconText: 'text-sky-600', chip: 'bg-sky-50 text-sky-700 border-sky-200' },
  settingsHref: '/prioritization/settings/compass-agent',
  role: "Catch where the priorities drift from the stated strategy.",
  goal: "Show where the ranked work and the stated objectives no longer line up, while it can still be fixed.",
  defaultSystemPrompt:
    'You are Compass, a strategy alignment analyst. You explain pre-computed alignment gaps to executives, referencing the specific objective and item names. Never invent data; lead with the gap that matters most.',
  howItWorks: [
    'Detection is exact over strategic_objectives and objective↔priority mappings — no model required for a finding to exist.',
    'Orphaned objective: a critical/high objective with nothing mapped to it.',
    'Under-owned: a critical/high objective touched only by weak (indirect) links — no primary owner.',
    'Unbacked work: active items mapped to no objective, and portfolio imbalance where a low-priority objective hoards effort.',
  ],
});

export const KEYSTONE_AGENT: AgentDefinition = findingAgent({
  agentType: 'keystone',
  name: 'Keystone',
  tagline: 'Leverage finder — surfaces the obscure item that unblocks everything',
  description:
    'Reads the dependency graph between your items and finds the leverage a ranked list never shows: the low-profile item that, directly or transitively, blocks a stack of downstream priorities — and the circular dependency chains that can never start. Sequence the keystone first and everything behind it moves.',
  icon: KeyRound,
  accent: { iconBg: 'bg-amber-100', iconText: 'text-amber-600', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  settingsHref: '/prioritization/settings/keystone-agent',
  role: "Find the obscure item that unblocks many others.",
  goal: "Show which items carry the structure, so they are decided on purpose.",
  defaultSystemPrompt:
    'You are Keystone, a leverage analyst. You explain pre-computed dependency-graph evidence to executives, emphasising sequencing leverage. Never invent edges or counts; lead with how many items the keystone unblocks.',
  howItWorks: [
    'Detection is exact graph maths over confirmed dependency edges — no model required for a finding to exist.',
    'Keystone: an item whose transitive downstream dependents exceed a threshold; low-altitude keystones blocking higher-altitude work rank highest.',
    'Dependency loop: a circular chain of blockers that can never be sequenced to start.',
    'Pair with Nexus, which proposes the dependency edges Keystone analyses.',
  ],
});

export const MERIDIAN_AGENT: AgentDefinition = findingAgent({
  agentType: 'meridian',
  name: 'Meridian',
  tagline: 'Execution drift — reconciles what you ratified with where energy actually goes',
  description:
    'Compares the ratified priority ranking with the execution activity flowing through your connected tools (Jira, Linear, GitHub — via change events). Meridian computes each item\'s share of the last 30 days of linked activity and speaks only when commitment and attention diverge: a top commitment nobody is touching, or an unranked item quietly absorbing the team\'s energy. Every finding is a prompt for inquiry, reviewed by a human — never an accusation.',
  icon: Crosshair,
  accent: { iconBg: 'bg-violet-100', iconText: 'text-violet-600', chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  settingsHref: '/prioritization/settings/meridian-agent',
  role: "Reconcile what was ratified with where the energy actually goes.",
  goal: "Show ratified commitments that are getting no activity, and activity on work nobody ratified.",
  defaultSystemPrompt:
    'You are Meridian, an execution-drift sentinel. You explain pre-computed commitment-vs-attention evidence to executives, citing ranks, shares, and event counts exactly. Frame every finding as a prompt for inquiry — never an accusation; work may live outside the connected tools. Never invent numbers; lead with the divergence.',
  howItWorks: [
    'Detection is exact maths over the ratified ranking and 30 days of connector change events joined to items via external-ID links — no model required for a finding to exist.',
    'Starved priority: a top-quartile commitment drawing near-zero linked activity while other items get plenty.',
    'Attention sink: an unranked or bottom-half item absorbing an outsized share of linked activity.',
    'Below a minimum event count Meridian says nothing — too little signal to accuse anyone. Findings are framed as questions to ask, not verdicts.',
  ],
});

export const CONDITION_WATCH_AGENT: AgentDefinition = findingAgent({
  agentType: 'condition_watch',
  name: 'Condition Watch',
  tagline: 'Governance sentinel — notices when the rules for changing the plan are bypassed',
  description:
    'Watches the two failure conditions of the Signal Framework: a material signal on record that no reprioritization act followed (a Condition B candidate), and a plan change with no material signal behind it (a Condition C candidate). Every finding is a question for the humans who hold the reprioritization right — "was this a deliberate hold?", "what signal motivated this change?" — never a verdict. Condition Watch never sets any condition and never touches authority state: the Plan-of-Record condition is set only by a human via set_por_condition, rationale required, never inferred.',
  icon: ShieldAlert,
  accent: { iconBg: 'bg-teal-100', iconText: 'text-teal-600', chip: 'bg-teal-50 text-teal-700 border-teal-200' },
  settingsHref: '/prioritization/settings/condition-watch-agent',
  role: "Notice when the rules for changing the plan are bypassed.",
  goal: "Ask the people who hold the reprioritization right about a signal nobody acted on, or a change no signal explains.",
  defaultSystemPrompt:
    'You are Condition Watch, a governance sentinel. You explain pre-computed condition candidates to the cycle owner as questions, never verdicts: a signal may have been deliberately held, and a change may have an off-record reason. Never claim a condition holds — the Plan-of-Record condition is set only by a human via set_por_condition, with a rationale. Never invent signals, acts, dates, or gaps; cite the labels and day counts exactly.',
  howItWorks: [
    'Detection is exact maths over recorded signals and governance acts within a 60-day lookback — no model required for a finding to exist.',
    'Condition B candidate: a material (critical/high) signal older than the 14-day policy window with no governance act after it — "was this a deliberate hold?"',
    'Condition C candidate: a governance act with no material signal in the 14 days before it — "what signal motivated this change?"',
    'Canon fidelity: candidates are questions for the humans holding the right. Condition Watch never sets a condition — the POR condition is set only by a human via set_por_condition, rationale required, never inferred.',
  ],
});


export const BEACON_AGENT: AgentDefinition = findingAgent({
  agentType: 'beacon',
  name: 'Beacon',
  tagline: 'External signal watcher — speaks only when the outside world touches your ratified plan',
  description:
    'Watches external sources for the entities your plan already depends on. Beacon derives its own watchlist — competitors, technologies, regulations, named customers, markets — from your ratified priorities, strategies, and plan assumptions (no configuration; entities are reviewable and dismissable), then sweeps the news daily. It is a witness, not a feed: a signal surfaces only when it is material to a currently ratified item or a live assumption, always with the evidence link attached.',
  icon: RadioTower,
  accent: { iconBg: 'bg-indigo-100', iconText: 'text-indigo-600', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  settingsHref: '/prioritization/settings/beacon-agent',
  role: "Watch outside sources for the entities the ratified plan depends on.",
  goal: "Speak only when something outside touches a ratified item or a live assumption, with the evidence attached.",
  defaultSystemPrompt:
    'You extract external entities a business plan depends on — competitors, technologies, regulations, named customers, markets — from ratified priorities, strategies, and assumptions. Extract only what the plan text actually depends on; never invent entities and never include internal project names.',
  howItWorks: [
    'The watchlist is derived, not configured: one metered LLM pass extracts up to 10 external entities from ratified item titles, strategies/objectives, and valid plan assumptions — each entry reviewable, with the plan refs it underpins.',
    'A daily server-side sweep fetches recent news per entity (hard-capped: ≤10 entities/run, ≤3 signals each, 14-day freshness) and dedupes by URL — the same story never surfaces twice.',
    'Materiality is deterministic: a signal raises a finding only while its entity still ties to a currently ratified item or a valid assumption; everything else is recorded silently.',
    'Findings accumulate for review with the evidence link attached (never summary-only); delivery is at most one summary per run, and only when a high-severity signal (2+ fresh stories on one entity) lands.',
  ],
  overrides: {
    evidenceModel: 'llm_primary',
    evidenceSources: [
      {
        key: 'workspace_record',
        label: 'The ratified plan',
        dataSent: 'Ratified item titles, strategies, objectives and plan assumptions — sent to the model once a run to derive the watchlist.',
        defaultEnabled: true,
        available: true,
      },
      {
        key: 'external_web',
        label: 'Public news (Google News)',
        dataSent: 'The watchlist entity names are sent to Google News as search terms. Nothing else leaves the workspace.',
        defaultEnabled: true,
        available: true,
      },
    ],
    supportedEvents: ['priority_list_approved'],
    idempotencyKey: 'the same story (by URL) never surfaces twice; findings accumulate for review',
    limits: { meterFeature: 'beacon', monthlyCap: 60, perRunObjectCap: 10, batchSize: 1, timeoutMs: 60_000, failurePosture: 'complete_with_errors' },
  },
});

export const REVIEW_DUE_AGENT: AgentDefinition = findingAgent({
  agentType: 'review_due',
  name: 'Review Due',
  tagline: 'Reconsideration watcher — surfaces the review dates the record set for itself',
  description:
    'Actuates the review clocks the record already carries. Decisions are recorded with a review-by date and reconsideration conditions; commitment terms are armed with kill criteria, review triggers, and expiry dates; confirmed signals carry a review date for their answer. Review Due scans those clocks on a schedule and, when one has passed, puts a candidate on the docket: a decision due for review, a tripped term awaiting its stop/rescope/keep call, a term past its review date, a signal past its review date with no response, a typed condition whose last reported reading fell outside its stated bound or that nobody has evaluated within the review cadence. It proposes only — it never trips a term, never reopens a decision, never answers a signal. Deciding stays with you.',
  icon: CalendarClock,
  accent: { iconBg: 'bg-orange-100', iconText: 'text-orange-600', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
  settingsHref: '/prioritization/settings/review-due-agent',
  role: "Put on the docket what the record says is due for reconsideration.",
  goal: "Make sure the review dates and conditions the record set for itself are acted on, not just displayed.",
  defaultSystemPrompt:
    'Review Due uses deterministic copy only — every finding is an exact date or status from the record. No model narration is involved.',
  howItWorks: [
    'Detection is exact dates and statuses — no model is involved anywhere: a decision is due when its review-by date has passed, a term when it tripped or its review date passed while armed, a signal when it is confirmed and past review with no response.',
    'Each candidate is proposed once: a fact you dismiss is never re-proposed, and a review date that is pushed out and later passes again counts as a new fact.',
    'Candidates that stop being due — because you resolved the term, reviewed the decision, answered the signal, or moved the condition through its normal path — are withdrawn from the docket automatically.',
    'Typed conditions are watched the same way: a reported reading outside a stated bound, or a live condition nobody has evaluated within the review cadence, becomes a candidate. Readings are reported by people — nothing here reads a metric system, and nothing moves a condition on its own.',
    'Nothing is ever tripped, reopened, or answered by the scan. Tripping a term, revising a decision, and answering a signal remain human acts through their existing governed paths.',
  ],
  overrides: {
    evidenceModel: 'deterministic_only',
    evidenceSources: [
      {
        key: 'workspace_record',
        label: 'The record\'s own review clocks',
        dataSent: 'Nothing leaves the workspace: decisions, commitment terms, signals and conditions are read where they are, and no model is used.',
        defaultEnabled: true,
        available: true,
      },
    ],
    supportedEvents: [],
    idempotencyKey: 'each fact is proposed once — a dismissed fact is never proposed again; a review date pushed out and passed again is a new fact',
    limits: { meterFeature: 'review_due', monthlyCap: 1, perRunObjectCap: 200, batchSize: 1, timeoutMs: 60_000, failurePosture: 'complete_with_errors' },
  },
});

export const FINDING_AGENTS: AgentDefinition[] = [CASSANDRA_AGENT, COMPASS_AGENT, KEYSTONE_AGENT, MERIDIAN_AGENT, CONDITION_WATCH_AGENT, BEACON_AGENT, REVIEW_DUE_AGENT];

export const AGENT_DEFINITIONS: Record<string, AgentDefinition> = {
  [AIR_TRAFFIC_CONTROLLER_AGENT_TYPE]: AIR_TRAFFIC_CONTROLLER,
  [TWINS_AGENT_TYPE]: TWINS,
  [NEXUS_AGENT_TYPE]: NEXUS,
  [SCRIBE_AGENT_TYPE]: SCRIBE,
  ...Object.fromEntries(FINDING_AGENTS.map((d) => [d.agentType, d])),
  [VALUE_ESTIMATOR_AGENT_TYPE]: VALUE_ESTIMATOR,
};

export function getAgentDefinition(agentType: string): AgentDefinition | undefined {
  return AGENT_DEFINITIONS[agentType];
}

// ── Behaviors: what a custom agent can be (spec §2 J) ─────────────────────────

export interface AgentBehavior {
  kind: AgentBehaviorKind;
  /** The type's name in the creator, in plain words. */
  label: string;
  /** One sentence: what an agent of this type does. */
  summary: string;
  /** Examples of agents an administrator might make with it. */
  examples: string[];
  /** The settings only this type has, in plain words — the type-specific part. */
  ownSettings: string[];
  /** The built-in agent whose job, outputs, runner and limits this type reuses. */
  base: AgentDefinition;
  /** Offered in the creator: an administrator may make a custom agent of this type. */
  customizable: boolean;
  /** Runs without a person (batch, events, schedule, tasks). False: only when someone uses it. */
  runsUnattended: boolean;
}

export const AGENT_BEHAVIORS: Record<AgentBehaviorKind, AgentBehavior> = {
  estimate_value: {
    kind: 'estimate_value',
    label: 'Estimate a value',
    summary:
      'Proposes a value for an item — effort, impact or any attribute with a scale — from similar items, your documents and connected history, for a person to accept, change or reject.',
    examples: ['A sizing guide that estimates effort the way your team does', 'An impact estimator that reads your strategy document'],
    ownSettings: [
      'Which attributes it may propose',
      'Estimate or score',
      'Where an accepted effort value goes',
      'How sure it must be, and how clear an item must be, before it proposes',
      'Whether it may propose with no similar item to go on',
    ],
    base: VALUE_ESTIMATOR,
    customizable: true,
    runsUnattended: true,
  },
  classify: {
    kind: 'classify',
    label: 'Classify items',
    summary:
      'Proposes each item\'s category and altitude from its text and your documents, for a person to accept, accept in part, or reject.',
    examples: ['A classifier that knows your own category examples', 'An intake triage agent that sorts new items as they arrive'],
    ownSettings: [
      'How sure it must be before it proposes',
      'Whether it may re-propose on items a person already classified',
    ],
    base: AIR_TRAFFIC_CONTROLLER,
    customizable: true,
    runsUnattended: true,
  },
  detect_cluster: {
    kind: 'detect_cluster',
    label: 'Find duplicates',
    summary:
      'Proposes groups of items that say the same thing, or close variants, for a person to accept as a cluster to resolve or dismiss.',
    examples: ['A duplicate finder for imported feedback', 'A variants finder that knows your product areas'],
    ownSettings: ['Whether it proposes variant groups as well as duplicates'],
    base: TWINS,
    customizable: true,
    runsUnattended: true,
  },
  propose_relationship: {
    kind: 'propose_relationship',
    label: 'Find relationships',
    summary:
      'Proposes how items relate — dependencies, parts of a whole, items that must move together — for a person to accept, which creates the relationship, or dismiss.',
    examples: ['A dependency finder for a program\'s items', 'A breakdown finder that knows your product structure'],
    ownSettings: ['Which relationship types it proposes', 'How sure it must be before it proposes'],
    base: NEXUS,
    customizable: true,
    runsUnattended: true,
  },
  draft_items: {
    kind: 'draft_items',
    label: 'Draft items from text',
    summary: 'Turns what a person types or says into draft items for them to keep, edit or skip.',
    examples: [],
    ownSettings: [],
    base: SCRIBE,
    // Used from the Scribe page only; there is no page to use a custom one from yet.
    customizable: false,
    runsUnattended: false,
  },
  find_issues: {
    kind: 'find_issues',
    label: 'Find issues',
    summary: 'Checks the workspace record with exact rules and raises findings, each with its evidence, for a person to accept or dismiss.',
    examples: [],
    ownSettings: [],
    base: COMPASS_AGENT,
    // Each finding agent is its own detector in code; a custom one would need its own rules.
    customizable: false,
    runsUnattended: true,
  },
};

export const AGENT_BEHAVIOR_LIST: AgentBehavior[] = Object.values(AGENT_BEHAVIORS);

/** The types the creator offers (spec §2 J). */
export const CUSTOMIZABLE_BEHAVIORS: AgentBehavior[] = AGENT_BEHAVIOR_LIST.filter((b) => b.customizable);

export function isAgentBehaviorKind(value: string): value is AgentBehaviorKind {
  return Object.prototype.hasOwnProperty.call(AGENT_BEHAVIORS, value);
}

// ── Custom agents ─────────────────────────────────────────────────────────────

/** Every template agent's report page (spec §5: one generic page). */
export function agentReportHref(agentType: string): string {
  return `/prioritization/agents/${agentType}`;
}

/** The agent type a custom agent's settings, runs, records and proposals are keyed by. */
export function customAgentType(customAgentId: string): string {
  return `custom_${customAgentId}`;
}

export function isCustomAgentType(agentType: string | null | undefined): boolean {
  return typeof agentType === 'string' && agentType.startsWith('custom_');
}

/** The identity half of a custom agent, as workspace_custom_agents stores it. */
export interface CustomAgentIdentity {
  id: string;
  name: string;
  description: string | null;
  behaviorKind: AgentBehaviorKind;
}

/**
 * A custom agent's full definition: its behavior's definition with the
 * administrator's name and words. Everything that holds rights — outputs,
 * evidence sources, events, review roles, limits — comes from the behavior
 * unchanged (spec §2 J: "a built-in with different words and documents, never
 * a new capability"). Its switches are read from agent_settings like any agent.
 */
export function definitionForCustomAgent(agent: CustomAgentIdentity): AgentDefinition {
  const behavior = AGENT_BEHAVIORS[agent.behaviorKind];
  const base = behavior.base;
  const agentType = customAgentType(agent.id);
  return {
    ...base,
    agentType,
    name: agent.name,
    tagline: behavior.label,
    description: agent.description?.trim() || base.description,
    kind: 'custom',
    icon: Bot,
    accent: { iconBg: 'bg-[var(--slate-dim)]', iconText: 'text-[var(--text-sec)]', chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--rule)]' },
    settingsHref: `/prioritization/settings/custom-agents/${agent.id}`,
    reportHref: agentReportHref(agentType),
  };
}

/**
 * The name a person reads for an agent on a working surface (the items list's
 * "Ask [agent] for these N"). A custom agent is called what its administrator
 * named it. A built-in whose name is a codename is called by its job in plain
 * words instead — "Duplicate finder", never "Twins" (plain-language rule).
 */
const PLAIN_BUILT_IN_NAMES: Record<string, string> = {
  [VALUE_ESTIMATOR_AGENT_TYPE]: 'Value Estimator',
  [AIR_TRAFFIC_CONTROLLER_AGENT_TYPE]: 'Classifier',
  [TWINS_AGENT_TYPE]: 'Duplicate finder',
  [NEXUS_AGENT_TYPE]: 'Relationship finder',
};

export function agentPlainName(def: Pick<AgentDefinition, 'agentType' | 'kind' | 'name'>): string {
  if (def.kind === 'custom') return def.name;
  return PLAIN_BUILT_IN_NAMES[def.agentType] ?? def.name;
}
